import { useQuery } from '@apollo/client';
import { Query } from 'graphql/generated/graphqlTypes';
import {
  GET_VISITOR_DETAILS_DOORS,
  GET_VISITOR_DETAILS_LOCK_GROUPS,
  GET_VISITOR_DETAILS_VISITOR_GROUPS
} from 'graphql/v2/queries';
import { TranslatedHookProps } from 'hooks/shared/types';
import { useEffect } from 'react';
import { getDoorsAssignedToVisitorGroups } from 'utils/Lock/Lock';
import { getLockGroupAssignedToVisitor } from 'utils/LockGroup/LockGroup';
import {
  filterOnlyCoContractorsGroups,
  filterVisitorGroupsWithMember,
  filterVisitorGroupsWithoutInternalGroups
} from 'utils/VisitorGroup/VisitorGroup';

interface Props extends TranslatedHookProps {
  visitorId: string;
}
const useVisitorDetailsAccessOverview = ({ handleFetchError, translation, visitorId }: Props) => {
  const {
    loading: visitorGroupsLoading,
    data: visitorGroupsData,
    error: visitorGroupsError
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_VISITOR_DETAILS_VISITOR_GROUPS, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  const allVisitorGroups = visitorGroupsData?.getVisitorGroupsForTenant;
  const visitorDetailsVisitorGroupsInternal = filterOnlyCoContractorsGroups(allVisitorGroups);
  const visitorDetailsVisitorGroupsAllWithoutInternal = filterVisitorGroupsWithoutInternalGroups(allVisitorGroups);
  const visitorGroupsWithVisitor = filterVisitorGroupsWithMember(allVisitorGroups, visitorId);
  const visitorDetailsVisitorGroupsContainingVisitor = visitorGroupsWithVisitor;

  const {
    loading: locationsLoading,
    data: locationsData,
    error: locationsError
  } = useQuery<Pick<Query, 'getLocationsForTenant'>>(GET_VISITOR_DETAILS_LOCK_GROUPS, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.locations_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const {
    loading: doorsLoading,
    data: doorsData,
    error: doorsError
  } = useQuery<Pick<Query, 'getDoorsForTenant'>>(GET_VISITOR_DETAILS_DOORS, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.doors_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  return {
    visitorDetailsVisitorGroupsContainingVisitor,
    visitorDetailsVisitorGroupsAll: visitorGroupsData?.getVisitorGroupsForTenant || [],
    visitorDetailsVisitorGroupsAllWithoutInternal,
    visitorDetailsVisitorGroupsInternal,
    loadingGetVisitorDetailsVisitorGroups: visitorGroupsLoading,
    visitorDetailsLockGroups: getLockGroupAssignedToVisitor(
      locationsData?.getLocationsForTenant || [],
      visitorDetailsVisitorGroupsContainingVisitor || []
    ),
    loadingGetVisitorDetailsLockGroups: locationsLoading,
    visitorDetailsDoors: getDoorsAssignedToVisitorGroups(
      visitorDetailsVisitorGroupsContainingVisitor || [],
      doorsData?.getDoorsForTenant || []
    ),
    loadingGetVisitorDetailsDoors: doorsLoading
  };
};

export default useVisitorDetailsAccessOverview;
